import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import WarningIcon from '@mui/icons-material/Warning';
import DurationBar from './DurationBar';
import type { ReconcilerVerify, ReconcilerVerifyOutcome } from '../../../../types/frontend';

const VERIFY_NOTE: Record<ReconcilerVerifyOutcome, string> = {
  CONVERGED: 'confirmed in AWS',
  SETTLING: 'settling in AWS',
  DRIFTED: 'drift detected',
};

export interface VerifyPhaseProps {
  verify: ReconcilerVerify;
  maxMs: number;
}

const VerifyPhase: React.FC<VerifyPhaseProps> = ({ verify, maxMs }) => {
  const { outcome } = verify;
  const drifted = outcome === 'DRIFTED';

  return (
    <Box sx={{ py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {outcome === 'CONVERGED' && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
          {outcome === 'SETTLING' && <SyncIcon sx={{ fontSize: 16, color: 'info.main' }} />}
          {drifted && <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Verify</Typography>
          <Typography variant="caption" color="text.secondary">
            · {verify.summary || VERIFY_NOTE[outcome]}
          </Typography>
        </Box>
        <DurationBar ms={verify.duration_ms} maxMs={maxMs} color={drifted ? 'warning.dark' : undefined} />
      </Box>

      {drifted && verify.observations.length > 0 && (
        <Box sx={{ mt: 1, pl: 3.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {verify.observations.map((obs, i) => (
            <Alert
              key={i}
              severity="warning"
              variant="outlined"
              sx={{
                py: 0.5,
                px: 1.5,
                '& .MuiAlert-icon': { fontSize: 16, mr: 1, py: 0.5 },
                '& .MuiAlert-message': { py: 0 },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                {obs.subject}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {obs.summary}
              </Typography>
              {obs.expected && obs.actual && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                  Expected: {obs.expected} · Actual: {obs.actual}
                </Typography>
              )}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default VerifyPhase;
